import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';

import EmbeddedIFrame from 'views/Frames/EmbeddedIFrame';
import SecondNavigationBar from 'components/Navigation/SecondNavigationBar';
import { Typography } from '@mui/material';
import {
  selectHasCDTBackOfficeApacAccess,
  selectHasCDTFrontOfficeApacAccess,
  selectIsUserApacBusinessEntity,
} from 'selectors/userSelector';
import { useRuntimeConfig } from 'config/useRuntimeConfig';
import { useElementHeight } from 'hooks/useElementHeight';
import { TOPBAR_HEIGHT } from 'services/constants';
import { User } from 'types/User';
import { GlobalState } from 'reducers/stateTypes';
import { routeList } from 'Routes';
import { ClmPortalIncomingPostMessageTypes } from 'utils/postMessageUtil';
import theme from 'theme';
import PageTitleToken from 'components/Common/PageTitleToken';
import { ZfTab, ZfTabList } from '@zafire/react';
import { useHost } from 'contexts/Host';
import { requestHostNavigation } from 'utils/browserUtil';
import { PartnerFormTabItems } from 'views/PartnerTabs/PrivateIndividualFormTabs';

const secondNavigationStyles = {
  title: {
    margin: theme.spacing(3, 0, 1, 0),
  },
};
enum DocumentCenterTabs {
  AGREEMENT_FORWARDING = 'agreement-forwarding',
  DOCUMENT_OVERVIEW = 'document-overview',
  AGREEMENT_ADMIN = 'agreement-admin',
}

const DocumentCenter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab: selectedTabFromUrl, id }: { tab?: DocumentCenterTabs; id?: string } = useParams();
  const isUserApac = useSelector(selectIsUserApacBusinessEntity);
  const runtimeConfig = useRuntimeConfig();
  const idPath = id ? `/${id}` : '';
  const user: User = useSelector((state: GlobalState) => state.user.userDetails);
  const userId = user?.username;
  const { isStandalone, isWena } = useHost();
  const [secondNavRef, secondNavHeight] = useElementHeight<HTMLDivElement>();
  const iframeHeight = `calc(100% - ${TOPBAR_HEIGHT + secondNavHeight}px)`;

  const hasCDTFrontOfficeApacAccess = useSelector(selectHasCDTFrontOfficeApacAccess);
  const hasCDTBackOfficeApacAccess = useSelector(selectHasCDTBackOfficeApacAccess);

  const handleRedirect = useCallback(
    (tab: DocumentCenterTabs) => {
      navigate(routeList.documentCenter(tab, id));
    },
    [navigate, id],
  );

  const handleTabChange = (selectedTabName: string) => {
    switch (selectedTabName) {
      case DocumentCenterTabs.AGREEMENT_FORWARDING:
        handleRedirect(DocumentCenterTabs.AGREEMENT_FORWARDING);
        break;
      case DocumentCenterTabs.DOCUMENT_OVERVIEW:
        handleRedirect(DocumentCenterTabs.DOCUMENT_OVERVIEW);
        break;
    }
  };

  useEffect(() => {
    if (isUserApac !== undefined) {
      if (hasCDTBackOfficeApacAccess) {
        handleRedirect(DocumentCenterTabs.AGREEMENT_ADMIN);
      } else if (DocumentCenterTabs.AGREEMENT_FORWARDING === selectedTabFromUrl && hasCDTFrontOfficeApacAccess) {
        handleRedirect(DocumentCenterTabs.AGREEMENT_FORWARDING);
      } else if (DocumentCenterTabs.DOCUMENT_OVERVIEW === selectedTabFromUrl) {
        handleRedirect(DocumentCenterTabs.DOCUMENT_OVERVIEW);
      } else {
        DocumentCenterTabs.AGREEMENT_FORWARDING === selectedTabFromUrl
          ? handleRedirect(DocumentCenterTabs.AGREEMENT_FORWARDING)
          : handleRedirect(DocumentCenterTabs.DOCUMENT_OVERVIEW);
      }
    }
  }, [selectedTabFromUrl, isUserApac, handleRedirect, hasCDTFrontOfficeApacAccess, hasCDTBackOfficeApacAccess]);

  useEffect(() => {
    const handleMessage = (event: any) => {
      const message = event.data.message;
      switch (message) {
        case ClmPortalIncomingPostMessageTypes.REDIRECT_DOCUMENT_TAB_SHOW_CONTENT: {
          handleRedirect(DocumentCenterTabs.DOCUMENT_OVERVIEW);
          break;
        }
        case ClmPortalIncomingPostMessageTypes.REDIRECT_AGREEMENT_FORWARDING: {
          handleRedirect(DocumentCenterTabs.AGREEMENT_FORWARDING);
          break;
        }
        case ClmPortalIncomingPostMessageTypes.REDIRECT_TO_PARTNER: {
          const path = `/partner/edit/${event.data.value.partnerUid}/core-data`;
          if (isWena) {
            requestHostNavigation(path);
            break;
          }
          // message from pcm-br-frontend
          window.open(path);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.OPEN_PARTNER_TAB: {
          // message from pcd-document-mgmt, e.g. to open the relations or account-relations tab
          const { partnerUid, tab } = event.data.value;
          const isTabValid = Object.values(PartnerFormTabItems).includes(tab);
          const path = `/partner/edit/${partnerUid}/${isTabValid ? tab : PartnerFormTabItems.CORE_DATA}`;
          if (isWena) {
            requestHostNavigation(path);
            break;
          }
          window.open(path);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.NEW_TAB_BR_PARTNER_RELATIONS: {
          const path = `/business-relation/edit/${event.data.value.brId}/br-relations`;
          if (isWena) {
            requestHostNavigation(path);
            break;
          }
          window.open(path);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.NEW_TAB_BR_CORE_DATA: {
          const { brId, brSource } = event.data.value;
          window.open(`${routeList.brCoreData(brId)}${brSource === 'CRM_CH_BR' ? '?brSource=CRM' : ''}`);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.OPEN_DISTRIBUTION_PCD2: {
          const { requestUuid } = event.data.value;

          window.open(`${runtimeConfig.configData.iframeUrls.pcdV2Iframe}/distribution/${requestUuid}`);
        }
        // No default
      }
    };
    window.addEventListener('message', handleMessage, false);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [userId]);

  return (
    <>
      <PageTitleToken />
      <div ref={secondNavRef}>
        {!hasCDTBackOfficeApacAccess && selectedTabFromUrl && (
          <SecondNavigationBar>
            {isStandalone && (
              <Typography variant="h1" color="primary" sx={secondNavigationStyles.title}>
                {t('topbar.documentCenter.title')}
              </Typography>
            )}
            <ZfTabList
              role="tabList"
              value={selectedTabFromUrl}
              onTabChange={(event) => handleTabChange(event.detail)}
              style={{ paddingTop: 'var(--padding-vertical-xs)' }}
            >
              {hasCDTFrontOfficeApacAccess && (
                <ZfTab
                  label={t('distributionCenter.agreement.forwarding')}
                  role="tab"
                  aria-label="Agreement Forwarding"
                  value={DocumentCenterTabs.AGREEMENT_FORWARDING}
                  id="document-center-agreement-forwarding-tab"
                />
              )}
              <ZfTab
                label={t('accounts.tabs.documentOverview.title')}
                role="tab"
                value={DocumentCenterTabs.DOCUMENT_OVERVIEW}
                id="document-center-agreement-tracking-tab"
              />
            </ZfTabList>
          </SecondNavigationBar>
        )}
      </div>
      {selectedTabFromUrl === DocumentCenterTabs.DOCUMENT_OVERVIEW && (
        <EmbeddedIFrame
          url={`${runtimeConfig.configData.iframeUrls.pcdV2Iframe}/documents-overview`}
          title="Document center"
          id="DocumentCenterIFrame"
          height={iframeHeight}
        />
      )}
      {selectedTabFromUrl === DocumentCenterTabs.AGREEMENT_FORWARDING && hasCDTFrontOfficeApacAccess && (
        <EmbeddedIFrame
          url={`${runtimeConfig.configData.iframeUrls.pcdClientDocumentTemplateIframe}/${selectedTabFromUrl}${idPath}`}
          title="Agreement Forwarding"
          id="AgreementForwardingIFrame"
          height={iframeHeight}
        />
      )}
      {hasCDTBackOfficeApacAccess && (
        <EmbeddedIFrame
          url={`${runtimeConfig.configData.iframeUrls.pcdClientDocumentTemplateIframe}/agreement-admin`}
          title="Document Administration"
          id="DocumentAdministrationIFrame"
          height={`calc(100% - ${TOPBAR_HEIGHT}px)`}
        />
      )}
    </>
  );
};

export default DocumentCenter;
